import type { UniverseFrame } from '@/lib/universe/store'

import {
    documentOffset,
    getFrame,
    getPhase,
    isIntroPlaying,
    onLayoutRefresh,
    subscribeFrame
} from '@/lib/universe/store'

export type GravityKind = 'text' | 'block' | 'image'

export interface GravityOptions {
    kind: GravityKind
    fall: boolean
    delay: number
}

interface GravityEntry extends GravityOptions {
    element: HTMLElement
    top: number
    left: number
    width: number
    height: number
    visible: boolean
    revealed: boolean
    styled: boolean
}

const BASE_DURATION: Record<GravityKind, number> = {
    text: 1500,
    block: 1500,
    image: 1800
}

const entries = new Map<Element, GravityEntry>()

let observer: IntersectionObserver | null = null
let stopFrame: (() => void) | null = null
let stopLayout: (() => void) | null = null
let lastPointerTarget: Element | null = null
let lastPointerTime = 0
let pointerTracking = false

const smoothstep = (edge0: number, edge1: number, value: number): number => {
    const t = Math.min(1, Math.max(0, (value - edge0) / (edge1 - edge0)))

    return t * t * (3 - 2 * t)
}

const measure = (entry: GravityEntry): void => {
    const offset = documentOffset(entry.element)

    entry.top = offset.top
    entry.left = offset.left
    entry.width = entry.element.offsetWidth
    entry.height = entry.element.offsetHeight
}

// Things come into focus out of the dark. Close to the hole they take a little longer.
const reveal = (entry: GravityEntry, index: number, frame: UniverseFrame): void => {
    const element = entry.element
    const cx = entry.left + entry.width / 2
    const cy = entry.top - window.scrollY + entry.height / 2
    const dx = (cx - frame.holeX) / frame.height
    const dy = (cy - frame.holeY) / frame.height
    const proximity = Math.exp(-(dx * dx + dy * dy) / 0.16)
    const slow = frame.reducedMotion ? 1 : 1 + 0.6 * proximity
    const duration = frame.reducedMotion ? 420 : BASE_DURATION[entry.kind] * slow * (frame.compact ? 0.85 : 1)
    const entering = getPhase() === 'entering' ? 420 : 0
    const delay = frame.reducedMotion ? 0 : entry.delay + entering + Math.min(index, 6) * 110 + proximity * 180

    element.style.setProperty('--g-duration', `${Math.round(duration)}ms`)
    element.style.setProperty('--g-delay', `${Math.round(delay)}ms`)
    element.dataset.gravity = 'revealed'
    entry.revealed = true
}

const handleIntersections = (records: Array<IntersectionObserverEntry>): void => {
    const frame = getFrame()
    const batch: Array<GravityEntry> = []
    // Nothing comes into focus while a page is leaving or the opening intro is still playing
    const leaving = getPhase() === 'leaving' || isIntroPlaying()

    records.forEach(record => {
        const entry = entries.get(record.target)

        if (!entry) return

        entry.visible = record.isIntersecting

        const enough = record.intersectionRatio >= 0.12 || record.intersectionRect.height >= window.innerHeight * 0.4

        if (record.isIntersecting && enough && !entry.revealed && !leaving) batch.push(entry)
    })

    batch.sort((a, b) => a.top - b.top || a.left - b.left)
    batch.forEach((entry, index) => reveal(entry, index, frame))
}

const clearFall = (entry: GravityEntry): void => {
    const style = entry.element.style

    style.transform = ''
    style.filter = ''
    style.opacity = ''
    entry.styled = false
}

// Leaving the screen on the side of the hole: a slight stretch towards it, a little warmer and dimmer, then gone
const applyFall = (entry: GravityEntry, progress: number, frame: UniverseFrame): void => {
    const amplitude = Math.min(1, Math.max(0.25, 220 / Math.max(entry.height, 1)))
    const eased = progress * progress
    const cx = entry.left + entry.width / 2
    const cy = entry.top - frame.scrollY + entry.height / 2
    const ux = frame.holeX - cx
    const uy = frame.holeY - cy
    const length = Math.hypot(ux, uy) || 1
    const theta = Math.atan2(uy, ux)
    const stretch = 1 + 0.2 * eased * amplitude
    const squeeze = 1 - 0.05 * eased * amplitude
    const pull = 16 * eased * amplitude
    const lag = (frame.fallEdge === 'top' ? 1 : -1) * eased * frame.height * 0.03
    const tx = (ux / length) * pull
    const ty = (uy / length) * pull + lag
    const style = entry.element.style

    style.transformOrigin = '50% 50%'
    style.transform = `translate3d(${tx.toFixed(2)}px, ${ty.toFixed(2)}px, 0) rotate(${theta.toFixed(4)}rad) scale(${stretch.toFixed(4)}, ${squeeze.toFixed(4)}) rotate(${(-theta).toFixed(4)}rad)`
    style.filter = `sepia(${(0.35 * progress).toFixed(3)}) brightness(${(1 - 0.6 * progress).toFixed(3)})`
    style.opacity = (1 - smoothstep(0.3, 1, progress)).toFixed(3)
    entry.styled = true
}

const fallProgress = (entry: GravityEntry, frame: UniverseFrame): number => {
    const height = frame.height
    const zone = height * (frame.fallEdge === 'top' ? 0.3 : 0.22)
    const chunk = Math.min(entry.height, height * 0.25)

    if (frame.fallEdge === 'top') {
        const reference = entry.top + entry.height - chunk / 2
        const start = Math.max(reference - zone, 0)
        const full = Math.max(reference, start + 140)

        return (frame.scrollY - start) / (full - start)
    }

    const reference = entry.top + chunk / 2

    // Nothing on the first screen can be falling out of the bottom yet
    if (reference < height) return 0

    const start = reference - height + zone

    return (start - frame.scrollY) / zone
}

const onFrame = (frame: UniverseFrame): void => {
    if (frame.phase === 'leaving') return

    entries.forEach(entry => {
        if (frame.reducedMotion || !entry.fall || !entry.revealed || !entry.visible) {
            if (entry.styled) clearFall(entry)

            return
        }

        const progress = fallProgress(entry, frame)

        if (progress <= 0.001) {
            if (entry.styled) clearFall(entry)

            return
        }

        applyFall(entry, Math.min(1, progress), frame)
    })
}

const trackPointer = (): void => {
    if (pointerTracking) return

    pointerTracking = true
    document.addEventListener('pointerdown', event => {
        lastPointerTarget = event.target as Element | null
        lastPointerTime = performance.now()
    }, { capture: true, passive: true })
}

const ensureRunning = (): void => {
    if (typeof window === 'undefined') return

    if (!observer) {
        observer = new IntersectionObserver(handleIntersections, { threshold: [0, 0.12, 0.3] })
    }

    if (!stopFrame) stopFrame = subscribeFrame(onFrame)

    if (!stopLayout) {
        stopLayout = onLayoutRefresh(() => {
            entries.forEach(measure)
        })
    }

    trackPointer()
}

export const registerGravity = (element: HTMLElement, options: GravityOptions): (() => void) => {
    ensureRunning()

    const entry: GravityEntry = {
        ...options,
        element,
        top: 0,
        left: 0,
        width: 0,
        height: 0,
        visible: false,
        revealed: element.dataset.gravity === 'revealed',
        styled: false
    }

    measure(entry)
    entries.set(element, entry)
    observer?.observe(element)

    return () => {
        observer?.unobserve(element)
        entries.delete(element)

        if (entries.size === 0) {
            stopFrame?.()
            stopFrame = null
        }
    }
}

// After the opening intro, everything already on screen comes into focus as it would have on load
export const replayReveals = (): void => {
    if (!observer) return

    const watcher = observer

    entries.forEach(entry => {
        if (entry.revealed) return

        watcher.unobserve(entry.element)
        watcher.observe(entry.element)
    })
}

// Leaving a page: what is on screen dims and stretches slightly towards the hole,
// starting with whatever the visitor just touched
export const collapseGravity = (duration: number): void => {
    if (typeof window === 'undefined') return

    const frame = getFrame()
    const height = window.innerHeight
    const recent = performance.now() - lastPointerTime < 2500 ? lastPointerTarget : null
    const focus = recent || document.activeElement
    const visible: Array<{ entry: GravityEntry, rect: DOMRect, priority: number, distance: number }> = []

    entries.forEach(entry => {
        if (!entry.revealed) return

        const rect = entry.element.getBoundingClientRect()

        if (rect.bottom < 0 || rect.top > height || rect.width === 0) return

        const cx = rect.left + rect.width / 2
        const cy = rect.top + rect.height / 2
        const priority = focus && entry.element.contains(focus) ? 0 : 1

        visible.push({ entry, rect, priority, distance: Math.hypot(frame.holeX - cx, frame.holeY - cy) })
    })

    visible.sort((a, b) => a.priority - b.priority || a.distance - b.distance)

    visible.forEach(({ entry, rect }, index) => {
        const element = entry.element
        const cx = rect.left + rect.width / 2
        const cy = rect.top + rect.height / 2
        const dx = frame.holeX - cx
        const dy = frame.holeY - cy
        const theta = Math.atan2(dy, dx)
        const delay = frame.reducedMotion ? 0 : Math.min(index * 30, duration * 0.25)
        const time = Math.max(240, duration - delay)
        const from = element.style.transform || 'translate3d(0px, 0px, 0px) rotate(0rad) scale(1, 1) rotate(0rad)'

        element.style.transformOrigin = '50% 50%'
        element.getAnimations().forEach(animation => animation.cancel())

        if (frame.reducedMotion) {
            element.animate([{ opacity: 1 }, { opacity: 0 }], { duration: time, fill: 'forwards' })

            return
        }

        element.animate([
            {
                transform: from,
                opacity: Number(element.style.opacity || 1),
                filter: element.style.filter || 'sepia(0) brightness(1)'
            },
            {
                transform: `translate3d(${(dx * 0.06).toFixed(1)}px, ${(dy * 0.06).toFixed(1)}px, 0px) rotate(${theta.toFixed(4)}rad) scale(1.14, 0.92) rotate(${(-theta).toFixed(4)}rad)`,
                opacity: 0,
                filter: 'sepia(0.45) brightness(0.3)'
            }
        ], {
            duration: time,
            delay,
            easing: 'cubic-bezier(0.45, 0, 0.75, 0.3)',
            fill: 'forwards'
        })
    })
}
